import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { Crown, ArrowRight } from "lucide-react";

const FORMATS = ["APA 7", "Vancouver", "Chicago", "MLA", "IEEE"];
const COVERS = [
  { id: "forest", label: "Bosque", className: "bg-forest" },
  { id: "copper", label: "Cobre", className: "bg-[#A67C52]" },
  { id: "sand", label: "Arena", className: "bg-[#D9C7A7]" },
  { id: "sage", label: "Salvia", className: "bg-[#7E9A7C]" },
  { id: "dusk", label: "Crepúsculo", className: "bg-[#4A4A55]" },
];

export default function NewProjectDialog({ open, onOpenChange, onCreated, locked }) {
  const [title, setTitle] = useState("");
  const [career, setCareer] = useState("");
  const [university, setUniversity] = useState("");
  const [citationFormat, setCitationFormat] = useState("APA 7");
  const [coverColor, setCoverColor] = useState("forest");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle("");
    setCareer("");
    setUniversity("");
    setCitationFormat("APA 7");
    setCoverColor("forest");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (title.trim().length < 3 || career.trim().length < 2) {
      toast.error("Completa al menos título y carrera.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", {
        title: title.trim(),
        career: career.trim(),
        university: university.trim() || null,
        citation_format: citationFormat,
        cover_color: coverColor,
      });
      toast.success("Proyecto creado. ¡Manos a la obra!");
      reset();
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo crear el proyecto";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (locked) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="new-project-locked-dialog"
          className="sm:max-w-md bg-paper border-rule"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink flex items-center gap-2">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-forest text-paper">
                <Crown size={14} className="text-[#E0C58A]" />
              </span>
              Has alcanzado el límite del plan gratis
            </DialogTitle>
            <DialogDescription className="text-ink-soft text-[14px]">
              Tu plan permite 1 proyecto de tesis. Pasa a Premium para crear cuantos quieras y desbloquear generaciones ilimitadas, subida de PDFs y plantillas oficiales.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <a
              data-testid="locked-upgrade-cta"
              href="/pricing"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
            >
              Ver planes Premium
              <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-project-dialog"
        className="sm:max-w-lg bg-paper border-rule"
      >
        <DialogHeader>
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
            Nuevo proyecto
          </p>
          <DialogTitle className="font-serif text-2xl text-ink leading-tight">
            Da el primer paso de tu tesis
          </DialogTitle>
          <DialogDescription className="text-ink-soft text-[13px]">
            Define el título tentativo y tu carrera. Podrás ajustar todo más tarde.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4 mt-2" data-testid="new-project-form">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
              Título tentativo
            </span>
            <input
              data-testid="new-project-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              minLength={3}
              maxLength={200}
              placeholder="Ej. Adopción de IA en escuelas públicas del Edo. de México"
              className="px-3.5 py-2.5 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
                Carrera
              </span>
              <input
                data-testid="new-project-career"
                value={career}
                onChange={(e) => setCareer(e.target.value)}
                required
                minLength={2}
                placeholder="Ej. Pedagogía"
                className="px-3.5 py-2.5 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
                Universidad (opcional)
              </span>
              <input
                data-testid="new-project-university"
                value={university}
                onChange={(e) => setUniversity(e.target.value)}
                placeholder="UNAM, IPN, ITESM…"
                className="px-3.5 py-2.5 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
              Formato de citación
            </span>
            <div className="flex flex-wrap gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`format-${f.replace(/\s/g, "")}`}
                  onClick={() => setCitationFormat(f)}
                  className={`px-3.5 py-1.5 rounded-full text-[12px] border transition-colors ${
                    citationFormat === f
                      ? "bg-forest text-paper border-forest"
                      : "bg-white text-ink-soft border-rule hover:border-forest/40"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
              Color de portada
            </span>
            <div className="flex flex-wrap gap-2.5">
              {COVERS.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  data-testid={`cover-${c.id}`}
                  onClick={() => setCoverColor(c.id)}
                  aria-label={c.label}
                  className={`relative h-9 w-9 rounded-full ${c.className} ring-2 ring-offset-2 ring-offset-paper transition-all ${
                    coverColor === c.id ? "ring-forest" : "ring-transparent"
                  }`}
                />
              ))}
            </div>
          </label>

          <DialogFooter className="mt-3 gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="new-project-cancel"
              className="px-5 py-2.5 rounded-full text-[13px] text-ink-soft hover:bg-paper-2 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="new-project-submit"
              disabled={submitting}
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all disabled:opacity-60"
            >
              {submitting ? "Creando…" : "Crear proyecto"}
              {!submitting && (
                <ArrowRight
                  size={13}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
